<script lang="ts" setup>
import SolidGift02 from '@docue/docue-ui-v2/icons/SolidGift02.vue'
import LineCheckCircle from '@docue/docue-ui-v2/icons/LineCheckCircle.vue'
import LineAlertCircle from '@docue/docue-ui-v2/icons/LineAlertCircle.vue'
import LineCalendarCheck02 from '@docue/docue-ui-v2/icons/LineCalendarCheck02.vue'

import { useSubscriptionProcessContext } from '~/composables/subscriptionProcess'

const emit = defineEmits<{
  exitRequested: []
}>()

const { selectedPlan, discountCode, paymentPeriodUnit, isInterestedInLegalOnboarding, showIntercom } = useSubscriptionProcessContext()

const { workspace, activeWorkspaceId, country, user } = useAuth()
const { t } = useI18n()
const { getRegionName, formatCurrency } = useIntl()
const { terms, privacyPolicy } = useLinks()

const selectedPlanPriceId = computed(() =>
  selectedPlan.value?.prices
    .find(({ period_unit }) => period_unit === paymentPeriodUnit.value)
    ?.id ?? '',
)

const bookingCalendarLink = computed(() => {
  if (!country.value)
    return undefined

  const linkMap: Record<App.Internals.Enums.Country, string | undefined> = {
    FI: 'https://calendar.app.google/7RfuSSXDaz31k4Ye7',
    GB: 'https://meetings.hubspot.com/hmcintyre',
    SE: 'https://calendar.app.google/2oR4GM6xwU7fhXsC6',
    DE: undefined,
    PL: undefined,
  }

  return linkMap[country.value]
})

const onboardingSessionValueText = computed(() => {
  if (!country.value)
    return undefined

  const valueMap: Record<App.Internals.Enums.Country, number | undefined> = {
    FI: 100,
    GB: 100,
    SE: 1000,
    DE: undefined,
    PL: undefined,
  }

  return formatCurrency(valueMap[country.value] || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
})

const paymentMethodText = computed(() => {
  if (!workspace.value?.attributes.paymentMethod)
    return

  return {
    card: t('payment-method.card'),
    invoice: t('payment-method.invoice'),
    partner: t('partner'),
    direct_debit: t('payment-method.directDebit'),
  }[workspace.value.attributes.paymentMethod]
})

const companyNameAndIdent = computed(() => [
  workspace.value?.attributes.billingCompanyName,
  workspace.value?.attributes.billingCompanyIdent
    ? `(${workspace.value.attributes.billingCompanyIdent})`
    : undefined,
].filter(Boolean).join(' '))

const firstAndLastName = computed(() => [
  workspace.value?.attributes.billingFirstName,
  workspace.value?.attributes.billingLastName,
].filter(Boolean).join(' '))

const address = computed(() => [
  workspace.value?.attributes.billingAddress,
  [
    workspace.value?.attributes.billingPostCode,
    workspace.value?.attributes.billingCity,
  ].filter(Boolean).join(' '),
  workspace.value?.attributes.billingCountry
    ? getRegionName(workspace.value.attributes.billingCountry)
    : undefined,
].filter(Boolean).join(', '))

const vatNumberText = computed(() => workspace.value?.attributes.billingVatNumber
  ? `${t('vat.input-label')} ${workspace.value.attributes.billingVatNumber}`
  : undefined)

const billingDetails = computed(() => ([
  companyNameAndIdent.value,
  firstAndLastName.value,
  address.value,
  workspace.value?.attributes.email,
  vatNumberText.value,
].filter(Boolean)))

const {
  mutate: updateWorkspace,
  isPending: isWorkspaceUpdatePending,
  isError: isWorkspaceUpdateError,
} = useMutationWorkspace.update()

const guidanceRequestEnabled = computed(() => !['DE', 'PL'].includes(workspace.value?.attributes.country as App.Internals.Enums.Country))
const billingPhone = ref(workspace.value?.attributes.billingPhone ?? '')

watch(workspace, (workspace, oldWorkspace) => {
  if (workspace?.attributes.billingPhone !== oldWorkspace?.attributes.billingPhone && (billingPhone.value === oldWorkspace?.attributes.billingPhone || billingPhone.value === ''))
    billingPhone.value = workspace?.attributes.billingPhone ?? ''
}, { deep: true })

const hasEnteredPhoneNumber = computed(() => !!billingPhone.value.length && billingPhone.value !== workspace.value?.attributes.billingPhone)

const isWorkspaceUpdatePendingDelayed = refDelayed(isWorkspaceUpdatePending, false)

const saveBillingPhone = async () => {
  if (!activeWorkspaceId.value || isWorkspaceUpdatePending.value || !hasEnteredPhoneNumber.value)
    return

  updateWorkspace({
    workspaceId: activeWorkspaceId.value,
    payload: {
      billingPhone: billingPhone.value.trim(),
    },
  })
}

const { warning, info } = useToast()
whenever(isWorkspaceUpdateError, () => warning(t('saving-failed')))

const {
  mutate: sendLegalOnboardingRequest,
  isPending: isSendingLegalOnboardingRequest,
  isError: isSendingLegalOnboardingRequestError,
  isSuccess: isLegalOnboardingRequestSent,
} = useMutationIntercom.sendLegalOnboardingRequest()

whenever(isInterestedInLegalOnboarding, () => sendLegalOnboardingRequest({
  payload: {
    phone: billingPhone.value ?? user.value?.attributes.phone,
  },
}), { immediate: true, once: true })

whenever(isLegalOnboardingRequestSent, () => info(t('subscribe.legalOnboarding.success')))
</script>

<template>
  <div class="flex h-full max-h-full flex-col overflow-y-auto overscroll-y-none">
    <div
      class="me-auto ms-auto w-full max-w-[1120px] px-4 @container md:px-6"
    >
      <div class="my-16 flex w-full flex-col flex-nowrap @2xl:flex-row">
        <div class="flex w-full flex-col gap-8 @2xl:w-1/2">
          <DTAlert
            v-if="isSendingLegalOnboardingRequest"
            variant="blue"
          >
            <DTSpinner
              :size="4"
              :weight="2"
            />
            <span class="ml-1">{{ t('subscribe.legalOnboarding.sending') }}…</span>
          </DTAlert>
          <DTAlert
            v-else-if="isSendingLegalOnboardingRequestError"
            variant="red"
          >
            <LineAlertCircle class="size-4 shrink-0" />
            <i18n-t
              keypath="subscribe.legalOnboarding.error"
              tag="span"
            >
              <template #tryAgain>
                <DTButton
                  variant="link"
                  size="sm"
                  dense
                  class="!text-red-700 hover:!text-red-800"
                  @click="() => sendLegalOnboardingRequest({ payload: { phone: billingPhone ?? user?.attributes.phone } })"
                >
                  {{ t('tryAgain') }}
                </DTButton>
              </template>
              <template #chatTrigger>
                <DTButton
                  variant="link"
                  size="sm"
                  dense
                  class="!text-red-700 hover:!text-red-800"
                  @click="showIntercom"
                >
                  {{ t('chat.text') }}
                </DTButton>
              </template>
            </i18n-t>
          </DTAlert>

          <div>
            <h1 class="flex items-center gap-3 pb-4 text-2xl font-semibold">
              <LineCheckCircle class="mt-1 size-6 shrink-0 text-green-500" />
              {{ t('thank-you') }}
            </h1>
            <p class="mt-8 text-sm text-gray-600">
              {{ t('subscribe.confirmed.order-confirmed-subtitle') }}
            </p>
          </div>

          <div class="flex flex-col gap-8">
            <form
              v-if="guidanceRequestEnabled"
              class="rounded-lg bg-cream p-5"
              @submit.prevent="saveBillingPhone"
            >
              <span class="mb-3 flex size-8 items-center justify-center rounded-full bg-red-400">
                <SolidGift02 class="size-4 text-white" />
              </span>

              <h2 class="mb-3 text-xl font-semibold">
                {{ capitalize(t('subscribe.confirmed.onboarding.bookingTitle', { value: onboardingSessionValueText })) }}
              </h2>
              <div class="mb-6 text-sm text-gray-600">
                <i18n-t
                  v-if="bookingCalendarLink"
                  keypath="subscribe.confirmed.onboarding.bookingDescription"
                >
                  <template #free>
                    <strong class="text-gray-800">{{ t('subscribe.confirmed.onboarding.free') }}</strong>
                  </template>
                </i18n-t>
                <template v-else>
                  {{ t('subscribe.confirmed.onboarding.description') }}
                </template>
              </div>

              <DTField
                :label="t('phone.label')"
                :description="t('phone.hint')"
              >
                <DTFieldGroup>
                  <DTFieldInput
                    v-model="billingPhone"
                    type="tel"
                    class="w-full"
                  />
                  <DTFieldGroupButton
                    :disabled="isWorkspaceUpdatePendingDelayed"
                    type="submit"
                    class="!text-gray-800"
                  >
                    {{ t('submit') }}
                    <DTSpinner
                      v-if="isWorkspaceUpdatePendingDelayed"
                      :size="4"
                      :weight="2"
                    />
                  </DTFieldGroupButton>
                </DTFieldGroup>
              </DTField>

              <template v-if="bookingCalendarLink">
                <DTDivider
                  :text="t('or')"
                  class="my-6"
                />

                <DTButton
                  :to="bookingCalendarLink"
                  external
                  target="_blank"
                  class="w-full"
                >
                  <LineCalendarCheck02 class="size-5" />
                  {{ capitalize(t('subscribe.confirmed.onboarding.bookingCta')) }}
                </DTButton>
              </template>
            </form>

            <template v-else>
              <DTDivider />

              <div>
                <h2 class="mb-1 font-semibold">
                  {{ t('billing-details') }}
                </h2>
                <p
                  v-for="text, index in billingDetails"
                  :key="index"
                  class="text-sm text-gray-600"
                >
                  {{ text }}
                </p>
              </div>

              <DTDivider />

              <div v-if="paymentMethodText">
                <h2 class="mb-1 font-semibold">
                  {{ t('payment-method.title') }}
                </h2>
                <div class="text-sm text-gray-600">
                  {{ paymentMethodText }}
                </div>
              </div>
            </template>

            <div class="flex w-full flex-col gap-8 @2xl:hidden">
              <DTDivider />

              <SubscriptionHelpCta v-if="guidanceRequestEnabled" />

              <SubscriptionCartSummary
                v-else
                v-model="discountCode"
                :selected-plan-price-id="selectedPlanPriceId"
                is-subscription-created
              />
            </div>

            <DTButton
              variant="blue"
              class="mt-12"
              :disabled="isWorkspaceUpdatePending || isSendingLegalOnboardingRequest"
              @click="emit('exitRequested')"
            >
              {{ t('subscribe.confirmed.cta') }}
            </DTButton>
          </div>
        </div>

        <div class="hidden @2xl:block @2xl:w-1/2">
          <div
            class="sticky top-10 space-y-4 @2xl:ml-6 @4xl:ml-24"
          >
            <SubscriptionHelpCta v-if="guidanceRequestEnabled" />

            <SubscriptionCartSummary
              v-else
              v-model="discountCode"
              :selected-plan-price-id="selectedPlanPriceId"
              is-subscription-created
            />
          </div>
        </div>
      </div>

      <div class="mb-10 flex items-center gap-2 py-8 md:mb-0">
        <DTButton
          variant="link"
          size="sm"
          dense
          external
          target="_blank"
          :to="privacyPolicy"
        >
          {{ t('privacy-policy') }}
        </DTButton>

        <DTButton
          variant="link"
          size="sm"
          dense
          external
          target="_blank"
          :to="terms"
        >
          {{ t('terms-of-service') }}
        </DTButton>
      </div>
    </div>
  </div>
</template>
